import {TooltipProps as MUITooltipProps, Stack, Tooltip, Typography} from "@mui/material";
import {ReactElement, useCallback, useEffect, useState} from "react";
import {IntlShape, MessageDescriptor, useIntl} from "react-intl";

type Condition = boolean | (() => boolean) | (() => Promise<boolean>);

type TooltipProps = Omit<MUITooltipProps, "children" | "title">;

type TranslationEntry = {
    translationId: MessageDescriptor;
    translationValues?: Record<string, any>;
};

type MultiTooltipWrapperTitle = {
    condition: Condition;
    translation: TranslationEntry;
};

type MultiTooltipWrapperTitleStyle = {
    fontSize: number;
    spacing: number;
};

export type MultiTooltipWrapperProps = {
    /** Array of titles to display. */
    tooltipTitles: MultiTooltipWrapperTitle[];
    /** Style of the titles shown in the tooltip component. */
    titleStyle?: MultiTooltipWrapperTitleStyle;
    /** If true, only the first title that has a translation will be shown. */
    showFirstOnly?: boolean;
    /** Props to pass to the MUI Tooltip component. */
    tooltipProps?: TooltipProps;
    /** Children to wrap with the tooltip. */
    children: ReactElement<any>;
};

const MultiTooltipWrapperTitleStyleDefault: MultiTooltipWrapperTitleStyle = {
    fontSize: 12,
    spacing: 1,
} as const;

const getBoolValueFromCondition = async (condition: Condition): Promise<boolean> => {
    if (typeof condition === "boolean") {
        return condition;
    } else {
        return await condition();
    }
};

const getTranslatedTitles = (
    activeIds: number[],
    translationEntries: TranslationEntry[],
    intl: IntlShape,
    showFirstOnly: boolean,
): string[] => {
    if (activeIds.length === 0) return [];

    if (showFirstOnly && activeIds.length > 1) {
        const firstId = activeIds.find((id) => translationEntries[id] !== undefined);
        const first = firstId !== undefined ? translationEntries[firstId] : undefined;
        if (firstId === undefined) return [];
        return [intl.formatMessage(first.translationId, first.translationValues)];
    }

    return activeIds
        .map((id) => intl.formatMessage(translationEntries[id].translationId, translationEntries[id].translationValues))
        .filter((title) => title !== undefined);
};

const getTranslationEntries = (tooltipTitles: MultiTooltipWrapperTitle[]): TranslationEntry[] => {
    return tooltipTitles.map((title) => ({
        translationId: title.translation.translationId,
        translationValues: title.translation.translationValues,
    }));
};

/**
 * Conditional wrapper for MUI Tooltip component that can display multiple titles depending on multiple conditions.
 **/
const MultiTooltipWrapper = ({
    tooltipTitles,
    titleStyle,
    showFirstOnly = false,
    tooltipProps,
    children,
}: MultiTooltipWrapperProps) => {
    const [translatedTitles, setTranslatedTitles] = useState<string[]>([]);
    const intl = useIntl();

    const updateTranslatedTitles = useCallback(async () => {
        const activeIds = await Promise.all(
            tooltipTitles
                .map(async (title, index) => ((await getBoolValueFromCondition(title.condition)) ? index : -1))
                .filter(async (id) => (await id) !== -1),
        );
        setTranslatedTitles(getTranslatedTitles(activeIds, getTranslationEntries(tooltipTitles), intl, showFirstOnly));
    }, [intl, showFirstOnly, tooltipTitles]);

    useEffect(() => {
        updateTranslatedTitles();
    }, [updateTranslatedTitles]);

    return (
        <ConditionalTooltipWrapper
            {...tooltipProps}
            title={<MultiTooltipWrapperTitles titles={translatedTitles} style={titleStyle} />}
            showTooltip={translatedTitles.length > 0}
        >
            {children}
        </ConditionalTooltipWrapper>
    );
};

const ConditionalTooltipWrapper = (props: MUITooltipProps & {showTooltip: boolean}) => {
    const {showTooltip, ...tooltipProps} = props;
    return showTooltip ? (
        <div>
            <Tooltip {...tooltipProps}>
                <div>{props.children}</div>
            </Tooltip>
        </div>
    ) : (
        <>{props.children}</>
    );
};

type MultiTooltipWrapperTitlesProps = {
    titles: string[];
    style: MultiTooltipWrapperTitleStyle;
};

const MultiTooltipWrapperTitles = ({titles, style}: MultiTooltipWrapperTitlesProps) => {
    return (
        <Stack direction="column" spacing={style?.spacing ?? MultiTooltipWrapperTitleStyleDefault.spacing}>
            {titles.map((title, index) => (
                <Typography
                    key={`title-item-${index}`}
                    fontSize={style?.fontSize ?? MultiTooltipWrapperTitleStyleDefault.fontSize}
                >
                    {title}
                </Typography>
            ))}
        </Stack>
    );
};

export default MultiTooltipWrapper;
